import ROUTES from '@/routes/routes';
import {
  Alert,
  BannerWrapper,
  DialogPosition,
  MenuListDataItem,
  ScoreCard,
  StackedList,
  StackedListType,
  Typography,
} from '@ecdlink/ui';
import { useHistory } from 'react-router';
import { activities as activityConstants } from './constants';
import {
  formatTextToSlug,
  getCommunityQuarterDescription,
  getStringFromClassNameOrId,
  useDialog,
} from '@ecdlink/core';
import { useCallback } from 'react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import OnlineOnlyModal from '@/modals/offline-sync/online-only-modal';
import { useSelector } from 'react-redux';
import { communitySelectors } from '@/store/community';
import {
  calculateTierPercentages,
  getTierDetails,
} from '@/utils/community/league-position';
import { LeagueType } from '@/constants/Community';
import { ActivityDetailsState } from './activity-details/index.types';
import { TeamTabState } from '../../types';
import {
  COMMUNITY_WALKTHROUGH_STEPS,
  communityWalkthroughSteps,
} from '@/pages/community/walkthrough/steps';
import { useWindowSize } from '@reach/window-size';
import { useWalkthroughContext } from '@/context/walkthrougContext';
import { dummyClinic } from '@/pages/community/walkthrough/community-dummy';
import {
  CommunityWalkthrough,
  useCommunityTourAdvance,
} from '@/pages/community/walkthrough';
import { CommunityRouteState } from '@/pages/community/community.types';

export const TeamPoints = () => {
  const realClinicDetails = useSelector(communitySelectors.getClinicSelector);

  const history = useHistory();

  const { isOnline } = useOnlineStatus();
  const dialog = useDialog();
  const advanceTour = useCommunityTourAdvance();

  const openOfflineDialog = useCallback(() => {
    dialog({
      color: 'bg-white',
      position: DialogPosition.Middle,
      render: (onSubmit) => (
        <OnlineOnlyModal
          onSubmit={() => {
            onSubmit();
          }}
        />
      ),
    });
  }, [dialog]);

  const today = new Date();

  const { height } = useWindowSize();

  const { quarterDescription } = getCommunityQuarterDescription(today);

  const {
    state: { run: isWalkthrough, tourActive },
  } = useWalkthroughContext();

  const useWalkthroughData = isWalkthrough || tourActive;
  const clinicDetails = useWalkthroughData
    ? (dummyClinic as unknown as typeof realClinicDetails)
    : realClinicDetails;

  const { tierName, tierColor, pointsToNextTier, nextTier } = getTierDetails(
    (clinicDetails?.league?.leagueTypeName as LeagueType) ?? LeagueType.League,
    clinicDetails?.points?.pointsTotal ?? 0
  );

  const { bronzePercentage, silverPercentage, goldPercentage } =
    calculateTierPercentages(
      (clinicDetails?.league?.leagueTypeName as LeagueType) ?? LeagueType.League
    );

  const activities = activityConstants.map((activity): MenuListDataItem => {
    const points =
      clinicDetails?.points?.points?.find(
        (point) => point.pointsCategoryId === activity.id
      )?.pointsTotal ?? 0;

    return {
      showIcon: true,
      title: activity.name,
      titleStyle: 'text-textDark',
      menuIconUrl: activity.iconUrl,
      menuIconClassName: 'border-0',
      svgIcon: activity.svgIcon,
      iconHexBackgroundColor: activity.iconHexBgColor,
      ...(activity.color
        ? { backgroundColor: activity.color }
        : { hexBackgroundColor: activity.hexColor }),
      subItem: String(points),
      onActionClick: () => {
        if (isOnline) {
          history.push(
            ROUTES.COMMUNITY.TEAM.POINTS.ACTIVITY_DETAILS.replace(
              ':activitySlug',
              formatTextToSlug(activity.name)
            ),
            { currentPoints: points } as ActivityDetailsState
          );
        } else {
          openOfflineDialog();
        }
      },
    };
  });

  return (
    <>
      {/* Joyride lives on Community root; remount here so the points step survives route change */}
      <CommunityWalkthrough />
      <BannerWrapper
        title="Points"
        subTitle={clinicDetails?.name}
        renderBorder
        displayHelp
        onHelp={() =>
          isOnline
            ? history.push(ROUTES.COMMUNITY.TEAM.INFO_PAGE)
            : openOfflineDialog()
        }
        onBack={() => {
          if (useWalkthroughData) {
            // Next tip is team leader on the Team tab
            advanceTour(COMMUNITY_WALKTHROUGH_STEPS.FIVE);
            history.push(ROUTES.COMMUNITY.ROOT, {
              activeTabIndex: 0,
            } as CommunityRouteState);
            return;
          }
          history.push(ROUTES.COMMUNITY.ROOT, {
            forceReload: false,
          } as TeamTabState);
        }}
        className="p-4 pt-6"
        renderOverflow={!useWalkthroughData}
        style={{ height: useWalkthroughData ? height : 'auto' }}
      >
        <Typography
          type="h2"
          text={`${clinicDetails?.name} - points earned`}
          color="textDark"
        />
        <Typography type="h4" color="textMid" text={quarterDescription} />
        <ScoreCard
          className="my-4"
          mainText={String(clinicDetails?.points?.pointsTotal ?? 0)}
          hint="points"
          currentPoints={clinicDetails?.points?.pointsTotal ?? 0}
          maxPoints={clinicDetails?.points?.maxPointsTotal ?? 0}
          barBgColour="uiLight"
          barColour={tierColor}
          bgColour="uiBg"
          barSize="medium"
          barDivides={[
            { widthPercentage: bronzePercentage },
            { widthPercentage: silverPercentage },
            { widthPercentage: goldPercentage },
          ]}
          barStatusChip={{
            backgroundColour: 'primary',
            borderColour: 'primary',
            textColour: 'white',
            text: tierName,
          }}
          textColour="black"
        />
        {!!nextTier && (
          <Alert
            type="info"
            message={`Earn ${pointsToNextTier} more points to get to ${nextTier.toLowerCase()}!`}
            className="mb-4"
          />
        )}
        <Typography
          className="my-4"
          type="h3"
          text="Activities"
          color="textDark"
        />
        {/* Explicit wrapper for Joyride spotlight (step 3 / FOUR) */}
        <div
          id={getStringFromClassNameOrId(
            communityWalkthroughSteps[COMMUNITY_WALKTHROUGH_STEPS.FOUR].target
          )}
        >
          <StackedList
            isFullHeight={false}
            className="flex flex-col gap-2"
            type={'MenuList' as StackedListType}
            listItems={useWalkthroughData ? activities.slice(0, 3) : activities}
          />
        </div>
      </BannerWrapper>
    </>
  );
};
